import type { ReactNode } from 'react';
import type { PaymentBrandIconId } from '../lib/stripePaymentMethodsData';

type Props = {
  id: PaymentBrandIconId;
  className?: string;
};

function Badge({
  children,
  bg,
  textClass = 'text-white',
  className = '',
  label,
}: {
  children: ReactNode;
  bg: string;
  textClass?: string;
  className?: string;
  label: string;
}) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`inline-flex min-h-8 min-w-[2.5rem] max-w-[7rem] items-center justify-center rounded-md px-1.5 py-1 text-center text-[9px] font-bold leading-tight tracking-tight shadow-sm sm:text-[10px] ${bg} ${textClass} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * Compacte merkbadges voor “wij accepteren” (functioneel; geen officiële vector-downloads).
 */
export function PaymentBrandIcon({ id, className = '' }: Props) {
  switch (id) {
    case 'visa':
      return (
        <Badge bg="bg-[#1434CB]" label="Visa" className={className}>
          VISA
        </Badge>
      );
    case 'mastercard':
      return (
        <span className={`inline-flex h-8 items-center ${className}`} aria-label="Mastercard" title="Mastercard">
          <span className="relative flex h-6 w-10">
            <span className="absolute left-0 top-0 h-6 w-6 rounded-full bg-[#EB001B]" />
            <span className="absolute left-3 top-0 h-6 w-6 rounded-full bg-[#F79E1B] opacity-95" />
          </span>
        </span>
      );
    case 'ideal':
      return (
        <Badge bg="bg-[#CC0066]" label="iDEAL" className={className}>
          iDEAL
        </Badge>
      );
    case 'bancontact':
      return (
        <Badge bg="bg-[#FFDE00]" textClass="text-[#1A1A1A]" label="Bancontact" className={className}>
          Bancontact
        </Badge>
      );
    case 'apple_pay':
      return (
        <Badge
          bg="bg-neutral-900 dark:bg-white"
          textClass="text-white dark:text-neutral-900"
          label="Apple Pay"
          className={className}
        >
          Apple Pay
        </Badge>
      );
    case 'google_pay':
      return (
        <Badge
          bg="bg-white ring-1 ring-ct-border dark:bg-ct-elevated"
          textClass="text-ct-text"
          label="Google Pay"
          className={className}
        >
          Google Pay
        </Badge>
      );
    case 'klarna':
      return (
        <Badge bg="bg-[#FFB3C7]" textClass="text-[#0B0516]" label="Klarna" className={className}>
          Klarna
        </Badge>
      );
    case 'stripe_link':
      return (
        <Badge bg="bg-[#635BFF]" label="Link" className={className}>
          link
        </Badge>
      );
    case 'blik':
      return (
        <Badge bg="bg-[#E6007E]" label="BLIK" className={className}>
          BLIK
        </Badge>
      );
    case 'eps':
      return (
        <Badge bg="bg-[#123772]" label="EPS" className={className}>
          eps
        </Badge>
      );
    default:
      return null;
  }
}

type RowProps = {
  ids: PaymentBrandIconId[];
  className?: string;
};

export function PaymentBrandIconRow({ ids, className = '' }: RowProps) {
  return (
    <div className={`flex flex-wrap items-center gap-2 sm:gap-2.5 ${className}`} role="group" aria-label="Geaccepteerde betaalmethoden">
      {ids.map((id) => (
        <PaymentBrandIcon key={id} id={id} />
      ))}
    </div>
  );
}
